import {
  Card,
  Box,
  Typography,
  useMediaQuery,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import VehiclesStyles from "./VehiclesStyles";
import { IPolicyVehicle } from "../../../../../models/user/policyDetails/PolicyDetails";
import CustomTable, {
  IColumn,
} from "../../../../../common/CustomTable/CustomTable";
import AddVehicleForm from "../../../../../common/AddVehicleForm/AddVehicleForm";
import {
  DeleteOutline,
  EditOutlined,
  MoreVertOutlined,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { PostRemoveVehicle } from "../../../../../store/user/policyChange/policyChangeActions";
import { RootState } from "../../../../../store/store";
import { useParams } from "react-router-dom";
import {
  formatDateToYYYYMMDD,
  isFeatureAvailable,
} from "../../../../../utils/Utility";
import UpdateGarageLocation from "../../Coverages/UpdateGarageLocation/UpdateGarageLocation";
import AlertDialog from "../../../../../components/AlertDialog/AlertDialog";
import MessageDialog from "../../../../../components/MessageDialog/MessageDialog";
import CustomIcon from "../../../../../common/CustomIcon/CustomIcon";
import { useTranslation } from "react-i18next";
import CustomDrawer from "../../../../../common/CustomDrawer/CustomDrawer";
import VehiclesMobileView from "./VehiclesMobileView/VehiclesMobileView";
import { setCalledReducerType } from "../../../../../store/common/CommonSlice";
import CustomSkeleton from "../../../../../common/CustomSkeleton/CustomSkeleton";

interface IProps {
  vehicles: IPolicyVehicle[];
}

interface IGarageAddress {
  addr1: string;
  city: string;
  stateProvCd: string;
  postalCode: string;
}

export const renderAddress = (address: IGarageAddress) => {
  if (
    !address?.addr1 &&
    !address?.city &&
    !address?.stateProvCd &&
    !address?.postalCode
  ) {
    return "NA";
  } else {
    const addressParts = [];

    if (address?.addr1 != null && address.addr1 !== "")
      addressParts.push(address.addr1);
    if (address?.city != null && address.city !== "")
      addressParts.push(address.city);
    if (address?.stateProvCd != null && address.stateProvCd !== "")
      addressParts.push(address.stateProvCd);
    if (address?.postalCode != null && address.postalCode !== "")
      addressParts.push(address.postalCode);

    if (addressParts.length > 0) {
      const lastPart = addressParts.pop();
      return addressParts.length > 0
        ? `${addressParts.join(", ")} ${lastPart}.`
        : `${lastPart}.`;
    }

    return "NA";
  }
};

function Vehicles({ vehicles }: IProps) {
  const dispatch = useAppDispatch();

  const { policyId, carrier, system } = useParams();

  const { t } = useTranslation();

  const isMobile = useMediaQuery("(max-width:600px)");

  const { userDetails } = useAppSelector((state) => state.userAuth);
  const { policyDetailsHeader } = useAppSelector(
    (state: RootState) => state.policyDetails
  );
  const { isSuccess, isError, calledReducerType, isLoading } = useAppSelector(
    (state) => state.common
  );
  const { features } = useAppSelector((state: RootState) => state.features);

  const [addVehcileFormOpen, setsetAddVehicleFormOpen] =
    useState<boolean>(false);
  const [selectedVehicle, setSelectedVehicle] = useState<IPolicyVehicle | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [updateCoveragesOpen, setUpdateCoveragesOpen] =
    useState<boolean>(false);

  const open = Boolean(anchorEl);

  const handleAddVehicle = useCallback(() => {
    setsetAddVehicleFormOpen(true);
    dispatch(setCalledReducerType(null));
  }, []);

  const handleMoreClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, vehicle: IPolicyVehicle) => {
      setAnchorEl(event.currentTarget);
      setSelectedVehicle(vehicle);
    },
    []
  );

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteVehicleClick = () => {
    handleMenuClose();
    setAlertDialogOpen(true);
  };

  const handleAlertDialogCancel = () => {
    setAlertDialogOpen(false);
  };

  const handleAlertDialogOkClick = () => {
    setAlertDialogOpen(false);
    if (
      selectedVehicle &&
      policyId &&
      userDetails &&
      userDetails.customerId &&
      policyDetailsHeader &&
      policyDetailsHeader.carrierPolicyNumber &&
      carrier &&
      system &&
      policyDetailsHeader.lobCode
    ) {
      dispatch(
        PostRemoveVehicle({
          additionalInterestDetails: {
            loanNumber: "",
            address: null,
            interestName: "",
            interestTypeCd: "",
            status: "",
            phoneInfo: "",
          },
          carrierPolicyNo: policyDetailsHeader?.carrierPolicyNumber,
          changeDt: formatDateToYYYYMMDD(new Date()),
          customerId: userDetails.customerId,
          detailDescription: t("Removing vehicle from the policy"),
          policyRef: policyId,
          carrier,
          system,
          lobCode:policyDetailsHeader.lobCode, 
          vehicle: {
            make: selectedVehicle?.make,
            model: selectedVehicle?.model,
            manufactureYr: selectedVehicle.manufactureYr,
            vehicleType: selectedVehicle.vehicleType,
            vin: selectedVehicle.vin,
            address: null,
          },
        })
      );
    }
  };

  const handleMessageModalClose = useCallback(() => {
    setMessageDialogOpen(false);
  }, []);

  const handleUpdateCoveragesClick = useCallback(() => {
    handleMenuClose();
    setUpdateCoveragesOpen(true);
  }, []);

  const columns: IColumn[] = [
    {
      label: t("Year"),
      accessorKey: "manufactureYr",
    },
    {
      label: t("Make"),
      accessorKey: "make",
    },
    {
      label: t("Model"),
      accessorKey: "model",
    },
    {
      label: t("VIN"),
      accessorKey: "vin",
    },
    {
      label: t("Garage Location"),
      Cell: (row: any) => {
        const address = row?.address;

        const garageAddress = renderAddress(address);
        return garageAddress;
      },
    },
    {
      label: "",
      accessorKey: "",
      Cell: (vehicle: any) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isFeatureAvailable({
            featureCodes: ["lob_pa_policies_update_coverages"],
            features,
          }) ? (
            <Tooltip
              title={t("Update Garage Location")}
              onClick={() => {
                setSelectedVehicle(vehicle);
                handleUpdateCoveragesClick();
              }}
            >
              <IconButton>
                <EditOutlined />
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          )}
          {isFeatureAvailable({
            featureCodes: ["lob_pa_policies_delete_vehicle"],
            features,
          }) ? (
            <Tooltip title={t("Remove Vehicle")}>
              <IconButton
                disabled={vehicles.length === 1}
                onClick={() => {
                  setSelectedVehicle(vehicle);
                  handleDeleteVehicleClick();
                }}
              >
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "policyChange/PostRemoveVehicle"
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType]);

  return (
    <>
      <Box 
        sx={{ display: "grid", gap: "20px" }}
        id="vehicles_section"
        data-test-id="vehicles_section"
        aria-label="Vehicles section"
      >
        <Box 
          sx={VehiclesStyles.headingAndButton}
          id="vehicles_header"
          data-test-id="vehicles_header"
          aria-label="Vehicles header"
        >
          <Box 
            sx={VehiclesStyles.vehicleHeading}
            id="vehicles_heading"
            data-test-id="vehicles_heading"
            aria-label="Vehicles heading"
          >
            <Box
              id="vehicles_icon"
              data-test-id="vehicles_icon"
              aria-label="Vehicles icon"
            >
              <CustomIcon iconName="Car" />
            </Box>
            <Typography 
              sx={VehiclesStyles.HeadingText}
              id="vehicles_title"
              data-test-id="vehicles_title"
              aria-label={`Vehicles (${vehicles.length})`}
            >
              {`${t("Vehicles")} (${vehicles.length})`}
            </Typography>
          </Box>

          {isFeatureAvailable({
            featureCodes: ["lob_pa_policies_add_vehicle"],
            features,
          }) && policyDetailsHeader?.carrierPolicyStatus !== "Cancelled" ? (
            <Button
              id="vehicles_add_button"
              data-test-id="vehicles_add_button"
              aria-label="Add new vehicle"
              variant="contained"
              sx={VehiclesStyles.addVehicleButton}
              onClick={handleAddVehicle}
            >
              {t("Add Vehicle")}
            </Button>
          ) : (
            <></>
          )}
        </Box>
        {isMobile ? (
          isLoading && calledReducerType === "policyDetails/GetPolicyRisks" ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                paddingY: "0.5rem",
                gap: "0.8rem ",
              }}
              id="vehicles_loading_skeleton"
              data-test-id="vehicles_loading_skeleton"
              aria-label="Loading vehicles data"
            >
              <CustomSkeleton height={120} count={3}/>
            </Box>
          ) : (
            <VehiclesMobileView
              vehicles={vehicles}
              displayMoreOptions={true}
              handleMoreClick={handleMoreClick}
            />
          )
        ) : (
          <CustomTable
            columns={columns}
            data={vehicles}
            displaySkeleton={
              isLoading && calledReducerType === "policyDetails/GetPolicyRisks"
            }
          />
        )}
      </Box>
      <Menu 
        anchorEl={anchorEl} 
        open={open} 
        onClose={handleMenuClose}
        id="vehicles_menu"
        data-test-id="vehicles_menu"
        aria-label="Vehicle options menu"
      >
        {isFeatureAvailable({
          featureCodes: ["lob_pa_policies_update_garage_location"],
          features,
        }) ? (
          <MenuItem 
            onClick={handleUpdateCoveragesClick}
            id="vehicles_update_location"
            data-test-id="vehicles_update_location"
            aria-label="Update garage location"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <EditOutlined sx={{ fontSize: "16px" }} />
              <Typography>{t("Update Garage Location")}</Typography>
            </Box>
          </MenuItem>
        ) : null}

        {isFeatureAvailable({
          featureCodes: ["lob_pa_policies_delete_vehicle"],
          features,
        }) ? (
          <MenuItem
            onClick={handleDeleteVehicleClick}
            disabled={vehicles.length === 1}
            id="vehicles_remove"
            data-test-id="vehicles_remove"
            aria-label="Remove vehicle"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <DeleteOutline sx={{ fontSize: "16px" }} />
              <Typography>{t("Remove")}</Typography>
            </Box>
          </MenuItem>
        ) : null}
      </Menu>
      <CustomDrawer
        open={addVehcileFormOpen}
        onClose={() => setsetAddVehicleFormOpen(false)}
        title={t("Add Vehicle")}
        content={
          <AddVehicleForm open={true} setOpen={setsetAddVehicleFormOpen} />
        }
        contentStyles={{ padding: 0 }}
      />
      <AlertDialog
        handleCancelClick={handleAlertDialogCancel}
        handleOkClick={handleAlertDialogOkClick}
        open={alertDialogOpen}
        title={t(
          "Removing this vehicle will end its coverage under this policy"
        )}
      />
      <MessageDialog
        handleClose={handleMessageModalClose}
        open={messageDialogOpen}
      />
      {selectedVehicle ? (
        <CustomDrawer
          open={updateCoveragesOpen}
          onClose={() => setUpdateCoveragesOpen(false)}
          title={t("Update Garage Location")}
          content={
            <UpdateGarageLocation
              open={true}
              setOpen={setUpdateCoveragesOpen}
              vehicle={selectedVehicle}
            />
          }
          contentStyles={{ padding: 0 }}
        />
      ) : (
        <></>
      )}
    </>
  );
}

export default Vehicles;
